import PropTypes from 'prop-types';
import React from 'react';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const tableStyle = {
  width: "100%",
  fontFamily: "Open Sans",
  fontSize: "14px",
  marginBottom: "30px",
};

const headerCellStyle = {
  padding: "10px 12px",
  fontWeight: "bold",
  borderBottom: "2px solid var(--sl-border-secondary)",
  textAlign: "left",
};

const cellStyle = {
  padding: "8px 12px",
  borderBottom: "1px solid var(--sl-border-secondary)",
};

const rightAlign = {
  ...cellStyle,
  textAlign: "right",
};

const headerRightAlign = {
  ...headerCellStyle,
  textAlign: "right",
};

const totalRowStyle = {
  fontWeight: "bold",
  backgroundColor: "var(--sl-bg-secondary)",
};

const pricePointCellStyle = {
  ...cellStyle,
  paddingLeft: "28px",
  color: "var(--sl-text-quaternary)",
  fontSize: "13px",
};

const pricePointRightStyle = {
  ...rightAlign,
  color: "var(--sl-text-quaternary)",
  fontSize: "13px",
};

const sectionTitleStyle = {
  fontSize: "16px",
  fontWeight: "bold",
  marginBottom: "10px",
  marginTop: "20px",
};

const TicketAuditTables = ({
  eventDetails,
  ticketTypes,
  ticketTypesTotalAvailable,
  ticketTypesTotalHeld,
  ticketTypesTotalSold,
  ticketTypesTotalComp,
  ticketTypesTotalSubTotal,
  salesBySource,
  salesBySourceTotal,
  salesByPaymentMethod,
  salesByPaymentMethodTotal,
  generatedAt,
}) => {
  return (
    <div style={{ padding: "0px 7px" }}>
      <div style={sectionTitleStyle}>Ticket Audit</div>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={headerCellStyle}>Event ID</th>
            <th style={headerCellStyle}>Event Details</th>
            <th style={headerRightAlign}>Capacity</th>
            <th style={headerRightAlign}>Sold</th>
            <th style={headerRightAlign}>Comp</th>
            <th style={headerRightAlign}>Unsold</th>
            <th style={headerRightAlign}>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={cellStyle}>{eventDetails.event_id}</td>
            <td style={cellStyle}>{eventDetails.event_details}</td>
            <td style={rightAlign}>{eventDetails.capacity}</td>
            <td style={rightAlign}>{eventDetails.sold}</td>
            <td style={rightAlign}>{eventDetails.comp}</td>
            <td style={rightAlign}>{eventDetails.unsold}</td>
            <td style={rightAlign}>{eventDetails.total}</td>
          </tr>
        </tbody>
      </table>

      <div style={sectionTitleStyle}>Ticket Types</div>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={headerCellStyle}>Ticket Name</th>
            <th style={headerRightAlign}>Available</th>
            <th style={headerRightAlign}>Sold</th>
            <th style={headerRightAlign}>Holds</th>
            <th style={headerRightAlign}>Comps</th>
            <th style={headerRightAlign}>Ticket Price</th>
            <th style={headerRightAlign}>Sub-Total</th>
          </tr>
        </thead>
        <tbody>
          {ticketTypes.map((tt, index) => (
            <React.Fragment key={index}>
              <tr>
                <td style={cellStyle}>{tt.ticket_name}</td>
                <td style={rightAlign}>{tt.available}</td>
                <td style={rightAlign}>{tt.sold}</td>
                <td style={rightAlign}>{tt.held}</td>
                <td style={rightAlign}>{tt.comp}</td>
                <td style={rightAlign}>{formatter.format(tt.ticket_price)}</td>
                <td style={rightAlign}>{formatter.format(tt.sub_total)}</td>
              </tr>
              {(tt.price_points || []).map((pp, ppIndex) => (
                <tr key={`${index}-pp-${ppIndex}`}>
                  <td style={pricePointCellStyle} colSpan={5}>
                    ↳ {pp.name || "Price change"} — {pp.sold_count_threshold != null
                      ? `after ${Number(pp.sold_count_threshold).toLocaleString('en-US')} tickets sold`
                      : `effective ${pp.starts_at}`}
                  </td>
                  <td style={pricePointRightStyle}>{formatter.format(pp.price)}</td>
                  <td style={pricePointRightStyle}></td>
                </tr>
              ))}
            </React.Fragment>
          ))}
          <tr style={totalRowStyle}>
            <td style={cellStyle}>Total</td>
            <td style={rightAlign}>{ticketTypesTotalAvailable}</td>
            <td style={rightAlign}>{ticketTypesTotalSold}</td>
            <td style={rightAlign}>{ticketTypesTotalHeld}</td>
            <td style={rightAlign}>{ticketTypesTotalComp}</td>
            <td style={rightAlign}></td>
            <td style={rightAlign}>{formatter.format(ticketTypesTotalSubTotal)}</td>
          </tr>
        </tbody>
      </table>

      <div className="row">
        <div className="col-12 col-md-6">
          <div style={sectionTitleStyle}>Sales Source</div>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Method</th>
                <th style={headerRightAlign}>Quantity Sold</th>
                <th style={headerRightAlign}>Total</th>
              </tr>
            </thead>
            <tbody>
              {salesBySource.map((s, index) => (
                <tr key={index}>
                  <td style={cellStyle}>{s.method}</td>
                  <td style={rightAlign}>{s.quantity_sold}</td>
                  <td style={rightAlign}>{formatter.format(s.total)}</td>
                </tr>
              ))}
              <tr style={totalRowStyle}>
                <td style={cellStyle}>Total</td>
                <td style={rightAlign}>{salesBySourceTotal.quantity_sold}</td>
                <td style={rightAlign}>{formatter.format(salesBySourceTotal.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="col-12 col-md-6">
          <div style={sectionTitleStyle}>Payment Method</div>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Payment Method</th>
                <th style={headerRightAlign}>Quantity Sold</th>
                <th style={headerRightAlign}>Total</th>
              </tr>
            </thead>
            <tbody>
              {salesByPaymentMethod.map((s, index) => (
                <tr key={index}>
                  <td style={cellStyle}>{s.payment_method}</td>
                  <td style={rightAlign}>{s.quantity_sold}</td>
                  <td style={rightAlign}>{formatter.format(s.total)}</td>
                </tr>
              ))}
              <tr style={totalRowStyle}>
                <td style={cellStyle}>Total</td>
                <td style={rightAlign}>{salesByPaymentMethodTotal.quantity_sold}</td>
                <td style={rightAlign}>{formatter.format(salesByPaymentMethodTotal.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: "40px", paddingTop: "16px", borderTop: "1px solid var(--sl-border-secondary)", textAlign: "center" }}>
        <div style={{ fontSize: "13px", fontWeight: "bold", color: "var(--sl-text-secondary)", marginBottom: "6px" }}>
          Official Ticket Audit
        </div>
        <div style={{ fontSize: "12px", color: "var(--sl-text-quaternary)", maxWidth: "620px", margin: "0 auto", lineHeight: "1.5" }}>
          This report was automatically generated and certified by Opendate, Inc. using the platform's official transaction ledger. It represents an accurate snapshot of ticketing activity as of {generatedAt}.
        </div>
        <div style={{ fontSize: "12px", color: "var(--sl-text-quaternary)", marginTop: "8px" }}>
          © {new Date().getFullYear()} Opendate, Inc. All rights reserved.
        </div>
      </div>
    </div>
  );
};

TicketAuditTables.propTypes = {
  eventDetails: PropTypes.object.isRequired,
  ticketTypes: PropTypes.array.isRequired,
  ticketTypesTotalAvailable: PropTypes.number.isRequired,
  ticketTypesTotalHeld: PropTypes.number.isRequired,
  ticketTypesTotalSold: PropTypes.number.isRequired,
  ticketTypesTotalComp: PropTypes.number.isRequired,
  ticketTypesTotalSubTotal: PropTypes.number.isRequired,
  salesBySource: PropTypes.array.isRequired,
  salesBySourceTotal: PropTypes.object.isRequired,
  salesByPaymentMethod: PropTypes.array.isRequired,
  salesByPaymentMethodTotal: PropTypes.object.isRequired,
  generatedAt: PropTypes.string,
};

export default TicketAuditTables;

